"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import {
  Star,
  ChevronLeft,
  Loader2,
  Send,
  Eye,
  MousePointerClick,
  Clock,
  CheckCircle,
  Mail,
  MessageSquare,
  ExternalLink,
  Settings,
  TrendingUp,
  Calendar,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useTranslations } from "@/hooks/useTranslations";
import Link from "next/link";

interface ReviewRequest {
  id: string;
  channel: string;
  status: string;
  sentAt: string | null;
  openedAt: string | null;
  clickedAt: string | null;
  createdAt: string;
  job: {
    id: string;
    title: string;
  };
  client: {
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
  };
}

interface ReviewStats {
  total: number;
  pending: number;
  sent: number;
  opened: number;
  clicked: number;
  completed: number;
  openRate: number;
  clickRate: number;
}

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
  sent: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  opened: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
  clicked: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  completed: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200",
};

function formatDate(dateString: string | null): string {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function ReviewsDashboardPage() {
  const router = useRouter();
  const { isOwner, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const t = useTranslations();

  const [reviewRequests, setReviewRequests] = useState<ReviewRequest[]>([]);
  const [stats, setStats] = useState<ReviewStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [dateRange, setDateRange] = useState<string>("30");

  // Fetch review requests
  const fetchReviewRequests = async () => {
    try {
      setIsLoading(true);

      const params = new URLSearchParams();
      if (statusFilter !== "all") {
        params.append("status", statusFilter);
      }

      // Calculate date range
      if (dateRange !== "all") {
        const days = parseInt(dateRange);
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - days);
        params.append("startDate", startDate.toISOString());
      }

      const response = await fetch(`/api/reviews?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setReviewRequests(data.data || []);
        setStats(data.stats || null);
      } else {
        throw new Error("Failed to fetch review requests");
      }
    } catch (error) {
      console.error("Error fetching review requests:", error);
      toast({
        title: t("common.error"),
        description: t("reviews.toast.loadError"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && isOwner) {
      fetchReviewRequests();
    }
  }, [authLoading, isOwner, statusFilter, dateRange]);

  // Check permissions
  if (!authLoading && !isOwner) {
    return (
      <div className="container mx-auto p-4 md:p-6 lg:p-8">
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle>{t("reviews.accessRestricted")}</CardTitle>
            <CardDescription>
              {t("reviews.accessRestrictedDescription")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => router.push("/")} variant="outline">
              <ChevronLeft className="h-4 w-4 mr-2" />
              {t("reviews.backToDashboard")}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Star className="h-8 w-8 text-yellow-500" />
            {t("reviews.title")}
          </h1>
          <p className="text-muted-foreground mt-2">
            {t("reviews.description")}
          </p>
        </div>
        <Button asChild variant="outline">
          <Link href="/settings/reviews">
            <Settings className="h-4 w-4 mr-2" />
            {t("reviews.settings")}
          </Link>
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t("reviews.stats.totalSent")}</CardTitle>
              <Send className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.sent}</div>
              <p className="text-xs text-muted-foreground">
                {t("reviews.stats.pending", { count: stats.pending })}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t("reviews.stats.opened")}</CardTitle>
              <Eye className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.opened}</div>
              <p className="text-xs text-muted-foreground">
                {t("reviews.stats.openRate", { rate: stats.openRate })}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t("reviews.stats.clicked")}</CardTitle>
              <MousePointerClick className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.clicked}</div>
              <p className="text-xs text-muted-foreground">
                {t("reviews.stats.clickRate", { rate: stats.clickRate })}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t("reviews.stats.performance")}</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.clickRate}%</div>
              <p className="text-xs text-muted-foreground">
                {t("reviews.stats.clickedThrough")}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>{t("reviews.recentRequests")}</CardTitle>
          <CardDescription>
            {t("reviews.recentRequestsDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">{t("reviews.filters.status")}:</span>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("reviews.filters.all")}</SelectItem>
                  <SelectItem value="pending">{t("reviews.filters.pending")}</SelectItem>
                  <SelectItem value="sent">{t("reviews.filters.sent")}</SelectItem>
                  <SelectItem value="opened">{t("reviews.filters.opened")}</SelectItem>
                  <SelectItem value="clicked">{t("reviews.filters.clicked")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">{t("reviews.filters.period")}:</span>
              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">{t("reviews.filters.last7Days")}</SelectItem>
                  <SelectItem value="30">{t("reviews.filters.last30Days")}</SelectItem>
                  <SelectItem value="90">{t("reviews.filters.last90Days")}</SelectItem>
                  <SelectItem value="all">{t("reviews.filters.allTime")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : reviewRequests.length === 0 ? (
            <div className="text-center py-8">
              <Star className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">{t("reviews.empty.title")}</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {t("reviews.empty.description")}
              </p>
              <Button asChild variant="outline">
                <Link href="/settings/reviews">
                  <Settings className="h-4 w-4 mr-2" />
                  {t("reviews.empty.configureSettings")}
                </Link>
              </Button>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("reviews.table.client")}</TableHead>
                    <TableHead>{t("reviews.table.job")}</TableHead>
                    <TableHead>{t("reviews.table.channel")}</TableHead>
                    <TableHead>{t("reviews.table.status")}</TableHead>
                    <TableHead>{t("reviews.table.sent")}</TableHead>
                    <TableHead>{t("reviews.table.activity")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reviewRequests.map((request) => (
                    <TableRow key={request.id}>
                      <TableCell>
                        <div className="font-medium">{request.client.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {request.client.email || request.client.phone || "-"}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Link
                          href={`/jobs/${request.job.id}`}
                          className="text-primary hover:underline flex items-center gap-1"
                        >
                          {request.job.title}
                          <ExternalLink className="h-3 w-3" />
                        </Link>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {request.channel === "email" ? (
                            <Mail className="h-4 w-4" />
                          ) : (
                            <MessageSquare className="h-4 w-4" />
                          )}
                          <span className="text-sm capitalize">
                            {request.channel}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="secondary"
                          className={STATUS_COLORS[request.status] || ""}
                        >
                          {request.status === "pending" && (
                            <Clock className="h-3 w-3 mr-1" />
                          )}
                          {request.status === "sent" && (
                            <Send className="h-3 w-3 mr-1" />
                          )}
                          {request.status === "opened" && (
                            <Eye className="h-3 w-3 mr-1" />
                          )}
                          {request.status === "clicked" && (
                            <MousePointerClick className="h-3 w-3 mr-1" />
                          )}
                          {request.status === "completed" && (
                            <CheckCircle className="h-3 w-3 mr-1" />
                          )}
                          {t(`reviews.status.${request.status}`)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(request.sentAt)}
                      </TableCell>
                      <TableCell>
                        <div className="text-xs text-muted-foreground space-y-1">
                          {request.openedAt && (
                            <div className="flex items-center gap-1">
                              <Eye className="h-3 w-3" />
                              {t("reviews.activity.opened")} {formatDate(request.openedAt)}
                            </div>
                          )}
                          {request.clickedAt && (
                            <div className="flex items-center gap-1 text-green-600">
                              <MousePointerClick className="h-3 w-3" />
                              {t("reviews.activity.clicked")} {formatDate(request.clickedAt)}
                            </div>
                          )}
                          {!request.openedAt && !request.clickedAt && "-"}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
